import Button from 'components/ui/Button';
import CollapsibleSection from 'components/ui/CollapsibleSection';
import FileUpload from 'components/ui/FileUpload';
import Flex from 'components/ui/Flex';
import Spinner from 'components/ui/Spinner';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import services from 'services';
import { adminSelectors, adminThunks, clearUploadResult } from 'store/reducers/adminSlice';
import { companiesSelector } from 'store/reducers/companiesSlice';
import { userSelector } from 'store/reducers/usersSlice';
import { styled } from 'styled-components';
import { downloadFile } from 'utils/downloadFile';
import SkuLocationUploadResult from './SkuLocationUploadResult';

const SkuLocationSection = () => {
    const inputRef = useRef<HTMLInputElement>(null);
    const uploadPromiseRef = useRef<{ abort: () => void } | null>(null);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const currentCompany = useAppSelector(companiesSelector.selectCurrentCompany);
    const isAdmin = useAppSelector(userSelector.selectIsAdmin);
    const uploadResult = useAppSelector(adminSelectors.selectUploadResult);
    const uploadError = useAppSelector(adminSelectors.selectUploadError);
    const uploadLoading = useAppSelector(adminSelectors.selectUploadLoading);

    const handleUpload = (file: File) => {
        const promise = dispatch(adminThunks.uploadSkuLocations(file));
        uploadPromiseRef.current = promise;
        promise
            .unwrap()
            .then(() => toast.success(t('admin.uploadComplete'), { autoClose: 3500, closeOnClick: true }))
            .catch((rejected) => {
                if (rejected === undefined) return; // Cancelled
                toast.error(t('admin.uploadFailed'), { autoClose: 3500, closeOnClick: true });
            });
    };

    const handleDrop = (files: File[]) => {
        if (files?.length > 0) handleUpload(files[0]);
    };

    const handleUploadFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleUpload(files[0]);
        }
    };

    const handleCancelUpload = () => uploadPromiseRef.current?.abort();

    const handleDownloadTemplate = async () => {
        const blob = await services.skuLocation.getTemplate();
        downloadFile(blob, 'sku-location-template.xlsx');
    };

    return (
        <Wrapper column gap="0">
            <CollapsibleSection openLabel="SKU Location Import">
                <Content>
                    <Flex column align="start">
                        <Text className="f3-400 mb-1 mt-0">SKU Location Import</Text>
                        <Button onClick={handleDownloadTemplate} border rounded>
                            {t('admin.downloadTemplate')}
                        </Button>
                    </Flex>
                    {isAdmin && (
                        <Text className="f3-400 mb-0" fontSize={12} color="textFaded">
                            {t('admin.uploadInfo', { company: currentCompany?.name ?? '' })}
                        </Text>
                    )}
                    <FileUploadWrapper>
                        {uploadLoading ? (
                            <LoadingOverlay column align="center" justify="center">
                                <Spinner size="medium" center useFullHeight={false} />
                                <Text className="f3-400" fontSize={14}>
                                    {t('admin.canNavigateAway')}
                                </Text>
                                <Button onClick={handleCancelUpload} border rounded>
                                    {t('cancel')}
                                </Button>
                            </LoadingOverlay>
                        ) : (
                            <FileUpload
                                multiple={false}
                                fileInputRef={inputRef}
                                handleDrop={handleDrop}
                                uploadFiles={handleUploadFiles}
                                hoverFontSize={16}
                                allowedFiletypes=".xlsx"
                            />
                        )}
                    </FileUploadWrapper>
                    {uploadError && <Text color="error">{uploadError}</Text>}
                    {uploadResult && (
                        <SkuLocationUploadResult result={uploadResult} onClose={() => dispatch(clearUploadResult())} />
                    )}
                </Content>
            </CollapsibleSection>
        </Wrapper>
    );
};

export default SkuLocationSection;

const Wrapper = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 1rem;
`;

const FileUploadWrapper = styled.div`
    width: 400px;
    height: 150px;
`;

const LoadingOverlay = styled(Flex)`
    width: 100%;
    height: 100%;
    gap: 12px;
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
`;
